import { useState } from "react";
import { X, Loader2, MessageSquare, PhoneCall } from "lucide-react";
import { createPortal } from "react-dom";
import { useIsMobile } from '@/core/hooks/useMediaQuery';
import { OtpDeliveryMode } from "../../application/services/accountMergingService";


  //  OTP Overlay Component
interface MergingOTPDialogProps {
  phoneNumber: string;
  blocking: boolean;
  isVerifyLoading: boolean;
  isResendLoading: boolean;
  otpMessage: { text: string; type: string };
  onVerify: (otp: string) => void;
  resendCountdown: number;
  onResend: (mode?: OtpDeliveryMode) => void;
  onClose: () => void;
  handleLogout: () => void;
}

export function MergingOTPDialog({ 
  phoneNumber, 
  blocking, 
  isVerifyLoading, 
  isResendLoading, 
  otpMessage, 
  onVerify, 
  resendCountdown,
  onResend, 
  onClose,
  handleLogout,
}: MergingOTPDialogProps) {
  const [otp, setOtp] = useState("");
  const [hideMessage, setHideMessage] = useState(false);  

  const [prevMessageText, setPrevMessageText] = useState(otpMessage.text);
  
  if (otpMessage.text !== prevMessageText) {
    setPrevMessageText(otpMessage.text);
    setHideMessage(false);
  }

  const isMobile = useIsMobile();
  const loadingState = isVerifyLoading || isResendLoading;

  return createPortal(
    <div className={`fixed inset-0 z-[60] bg-black/40 flex justify-center ${
        isMobile ? "items-end" : "items-center"
      }`}
      onClick={(e) => {
        if (e.target === e.currentTarget && !blocking && !loadingState) {
          onClose();
        }
      }}
    >
      <div className={`bg-white w-full flex flex-col shadow-xl transition-all transform ${
          isMobile 
            ? `rounded-t-2xl p-[20px] animate-in slide-in-from-bottom-4 duration-300 ${blocking ? "pt-[40px]": ""}` 
            : "max-w-lg rounded-md p-6 mx-4"
        }`}
      >
        <div className="flex flex-col justify-between items-start">
          {!blocking && (
            <button
              onClick={onClose}
              disabled={loadingState}
              className="self-end h-min text-sm text-gray-600 font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          )}
          <p className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold mb-2`}> Verify {blocking ? "your number" : "number +91"} {phoneNumber} </p>
        </div>
        <p className="text-sm text-gray-600 mb-2">
          We have sent an OTP on your mobile number & email. Please enter the OTP below. 
        </p>

        <div className="flex flex-col">
          <div className={`flex ${isMobile ? 'flex-col' : 'gap-2 h-[50px]'}`}>
            
            <div className="relative w-full h-[50px]">
              <input
                type="text"
                maxLength={4}
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => {
                  if (/^\d*$/.test(e.target.value)) {
                    setOtp(e.target.value);
                    setHideMessage(true);
                  }
                }}
                disabled={loadingState}
                className={`border px-3 py-2 w-full h-full outline-none focus:border-brand disabled:bg-gray-50 transition-colors border-gray-300
                  ${!hideMessage && otpMessage.text ? 'pr-[130px]' : ''}`}
              />
              
              {!hideMessage && otpMessage.text && (
                <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-right leading-tight max-w-[200px] ${
                  otpMessage.type === "error" ? "text-red-500" : "text-green-600"
                }`}>
                  {otpMessage.text}
                </span>
              )}
            </div>

            {!isMobile && (
              <div className="flex justify-end gap-3 shrink-0 h-[50px]">
              <button 
                disabled={otp.length < 4 || loadingState}
                onClick={() => onVerify(otp)}
                className="min-w-[140px] h-full flex items-center justify-center bg-hover px-5 py-2 text-md bg-brand text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isVerifyLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Submit
              </button>
            </div>
            )}
        </div>
          <div className="flex justify-between text-gray-600">
            {blocking ? (
              <div className={`items-center flex gap-2 ${isMobile ? 'mt-2 mb-8' : 'mt-2'}`}>
                {resendCountdown > 0 ? (
                  <span className="text-gray-400">Resend in {resendCountdown}s</span>
                ) : (
                  <>
                    Resend OTP via
                    <button onClick={() => onResend('call')} disabled={loadingState} className="flex items-center gap-2 text-[#4a90e2]">
                      <PhoneCall size={16} /> Call
                    </button>
                    <div className="w-px h-3 bg-gray-500" />
                    <button onClick={() => onResend('text')} disabled={loadingState} className="flex items-center gap-2 text-[#4a90e2]">
                      <MessageSquare size={16} /> Message
                    </button>
                  </>
                )}
              </div>
            ) : (
              <button
                onClick={() => onResend()}
                disabled={loadingState || resendCountdown > 0}
                className={`text-[#4a90e2] w-fit flex items-center text-sm hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed ${isMobile ? 'mt-2 mb-8' : 'mt-2'
                  }`}
              >
                {isResendLoading && <Loader2 className="w-3 h-3 mr-2 animate-spin" />}
                {resendCountdown > 0 ? `Resend in ${resendCountdown}s` : "Resend OTP"}
              </button>
            )}

            {blocking && !isMobile && <button onClick={handleLogout} className={`text-[#4a90e2] ${isMobile ? 'mt-2 mb-8' : 'mt-2'}`}>
              Logout
            </button>}
          </div>
          {isMobile && (
            <button 
              disabled={otp.length < 4 || loadingState}
              onClick={() => onVerify(otp)}
              className="w-full h-[50px] flex items-center justify-center bg-hover text-md bg-brand text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isVerifyLoading && <Loader2 className="w-5 h-5 mr-2 animate-spin" />}
              Submit
            </button>
          )}

          {isMobile && blocking && <button onClick={handleLogout} className={`mt-2 text-[#4a90e2]`}>
            Logout
          </button>}
        </div>
      </div>
    </div>,
    document.body
  );
}
